export function applyValidationToIdentifier(doc, validation, nowIso) {
    if (validation.status === 'VALID' && doc.draft !== undefined) {
        const stagedDraft = doc.draft;
        const activeLinks = stagedDraft.links
            .filter((link) => link.removed !== true)
            .map(({ removed: _removed, ...cleanLink }) => cleanLink);
        const nextVersion = doc.version + 1;
        const draftStatus = stagedDraft.status ?? 'ACTIVE';
        const effectiveStatus = doc.recallOverride?.active === true ? 'RECALLED' : draftStatus;
        const snapshot = {
            title: stagedDraft.title,
            ...(stagedDraft.brand !== undefined ? { brand: stagedDraft.brand } : {}),
            status: effectiveStatus,
            defaultUrl: stagedDraft.defaultUrl,
            links: activeLinks,
            rules: stagedDraft.rules ?? [],
            ...(doc.recallOverride !== undefined ? { recallOverride: doc.recallOverride } : {}),
            version: nextVersion,
            snapshotAt: nowIso,
        };
        const { draft: _removedDraft, ...rest } = doc;
        return {
            ...rest,
            title: stagedDraft.title,
            ...(stagedDraft.brand !== undefined ? { brand: stagedDraft.brand } : {}),
            status: effectiveStatus,
            defaultUrl: stagedDraft.defaultUrl,
            links: activeLinks,
            rules: stagedDraft.rules ?? [],
            version: nextVersion,
            validation,
            lastValidSnapshot: snapshot,
            updatedAt: nowIso,
        };
    }
    return {
        ...doc,
        validation,
        updatedAt: nowIso,
    };
}
export function applyValidationToQualifier(doc, validation, nowIso) {
    if (validation.status === 'VALID' && doc.draft !== undefined) {
        const stagedDraft = doc.draft;
        const nextVersion = doc.version + 1;
        const snapshot = {
            title: stagedDraft.title,
            ...(stagedDraft.brand !== undefined ? { brand: stagedDraft.brand } : {}),
            status: stagedDraft.status,
            defaultUrl: stagedDraft.defaultUrl,
            links: stagedDraft.links,
            rules: stagedDraft.rules,
            version: nextVersion,
            snapshotAt: nowIso,
        };
        const { draft: _removedDraft, ...rest } = doc;
        return {
            ...rest,
            label: stagedDraft.title,
            status: stagedDraft.status,
            defaultUrl: stagedDraft.defaultUrl,
            links: stagedDraft.links,
            rules: stagedDraft.rules,
            version: nextVersion,
            validation,
            lastValidSnapshot: snapshot,
            updatedAt: nowIso,
        };
    }
    return {
        ...doc,
        validation,
        updatedAt: nowIso,
    };
}
export function isIdentifierRecalled(record) {
    return record.status === 'RECALLED' || record.recallOverride?.active === true;
}
function toTimestampIsoString(value, fallbackIso) {
    if (typeof value === 'string' && value.length > 0) {
        return value;
    }
    if (typeof value === 'object' &&
        value !== null &&
        'toDate' in value &&
        typeof value.toDate === 'function') {
        try {
            return value.toDate().toISOString();
        }
        catch {
            return fallbackIso;
        }
    }
    return fallbackIso;
}
const DEFAULT_PRIMARY_AI_CODE = '01';
export function coerceFirestoreIdentifierDoc(raw, params) {
    const rawDraft = typeof raw['draft'] === 'object' && raw['draft'] !== null && !Array.isArray(raw['draft'])
        ? raw['draft']
        : undefined;
    const draft = rawDraft
        ? {
            title: typeof rawDraft['title'] === 'string' ? rawDraft['title'] : 'Staged Draft',
            ...(typeof rawDraft['brand'] === 'string' ? { brand: rawDraft['brand'] } : {}),
            status: (typeof rawDraft['status'] === 'string'
                ? rawDraft['status']
                : 'ACTIVE'),
            defaultUrl: typeof rawDraft['defaultUrl'] === 'string' ? rawDraft['defaultUrl'] : '',
            links: Array.isArray(rawDraft['links']) ? rawDraft['links'] : [],
            rules: Array.isArray(rawDraft['rules'])
                ? rawDraft['rules']
                : [],
            submittedBy: typeof rawDraft['submittedBy'] === 'string' ? rawDraft['submittedBy'] : 'system',
            submittedAt: typeof rawDraft['submittedAt'] === 'string' ? rawDraft['submittedAt'] : params.nowIso,
        }
        : undefined;
    const createdAt = toTimestampIsoString(raw['createdAt'], params.nowIso);
    const updatedAt = toTimestampIsoString(raw['updatedAt'], params.nowIso);
    const createdBy = typeof raw['createdBy'] === 'string' ? raw['createdBy'] : (draft?.submittedBy ?? 'system');
    const updatedBy = typeof raw['updatedBy'] === 'string' ? raw['updatedBy'] : (draft?.submittedBy ?? createdBy);
    const existingVersion = typeof raw['version'] === 'number' && Number.isInteger(raw['version']) ? raw['version'] : 0;
    const existingValidation = raw['validation'];
    const existingGs1Check = raw['gs1Check'];
    const existingSnapshot = raw['lastValidSnapshot'];
    const existingRecall = raw['recallOverride'];
    const existingSector = raw['sector'];
    const existingBrand = typeof raw['brand'] === 'string' ? raw['brand'] : undefined;
    return {
        identifierKey: typeof raw['identifierKey'] === 'string' ? raw['identifierKey'] : params.identifierKey,
        orgId: typeof raw['orgId'] === 'string' ? raw['orgId'] : params.orgId,
        workspaceId: typeof raw['workspaceId'] === 'string' ? raw['workspaceId'] : params.workspaceId,
        ai: typeof raw['ai'] === 'string' ? raw['ai'] : (params.defaultAi ?? DEFAULT_PRIMARY_AI_CODE),
        primaryKey: typeof raw['primaryKey'] === 'string' ? raw['primaryKey'] : '',
        title: typeof raw['title'] === 'string' ? raw['title'] : (draft?.title ?? 'Staged Draft'),
        ...(existingBrand !== undefined ? { brand: existingBrand } : {}),
        ...(existingSector !== undefined ? { sector: existingSector } : {}),
        status: (typeof raw['status'] === 'string' ? raw['status'] : 'DRAFT'),
        defaultUrl: typeof raw['defaultUrl'] === 'string' ? raw['defaultUrl'] : (draft?.defaultUrl ?? ''),
        links: Array.isArray(raw['links']) ? raw['links'] : [],
        rules: Array.isArray(raw['rules']) ? raw['rules'] : [],
        ...(existingRecall !== undefined ? { recallOverride: existingRecall } : {}),
        version: existingVersion,
        validation: existingValidation ?? {
            status: 'PENDING',
            validatedAt: params.nowIso,
            errors: [],
            warnings: [],
        },
        ...(existingGs1Check !== undefined ? { gs1Check: existingGs1Check } : {}),
        ...(existingSnapshot !== undefined ? { lastValidSnapshot: existingSnapshot } : {}),
        ...(draft !== undefined ? { draft } : {}),
        createdBy,
        createdAt,
        updatedAt,
        updatedBy,
    };
}
//# sourceMappingURL=callable-schemas.js.map